/**
 * Business AI & Cyber Safety Support — /business-ai-cyber-support
 * "Light Atelier" design: warm ivory base, premium photo composition,
 * ambient teal/peach gradient washes, glass snapshot card, mouse-tracked
 * spotlight, animated counters, spring-eased tab swap, scroll-reveal.
 *
 * Backend + Meta Pixel + Stripe + Auth + existing routes are NOT touched.
 */
import React, { useEffect, useMemo, useRef, useState, useCallback } from 'react';
import { Link } from 'react-router-dom';
import axios from 'axios';
import {
  ArrowRight, Sparkles, Workflow, ShieldCheck, Target, BookOpen, Megaphone,
  Lock, Bot, FileText, Phone, Mail, CheckCircle2, X, Loader2, Check, Send,
} from 'lucide-react';
import { API_URL } from '../config';
import '../styles/BusinessAICyberSupport.css';

const CONTACT_EMAIL = 'allsecured.com.au@proton.me';
const CONTACT_PHONE_DISPLAY = '0459 701 393';
const CONTACT_PHONE_TEL = 'tel:0459701393';
const PAGE_URL = 'https://allsecured.com.au/business-ai-cyber-support';

// ----------------------------------------------------------------------------
// IMAGES — Unsplash, royalty-free, warm-toned and human-centred (no stock cliché)
// ----------------------------------------------------------------------------
const IMG = {
  scenario1: 'https://images.unsplash.com/photo-1551836022-d5d88e9218df?auto=format&fit=crop&w=1600&q=85', // calm keyboard
  scenario2: 'https://images.unsplash.com/photo-1521737711867-e3b97375f902?auto=format&fit=crop&w=1600&q=85', // candid team
  scenario3: 'https://images.unsplash.com/photo-1556761175-5973dc0f32e7?auto=format&fit=crop&w=1600&q=85', // meeting
};

// ----------------------------------------------------------------------------
// SCENARIOS
// ----------------------------------------------------------------------------
const SCENARIOS = [
  {
    id: 'admin-efficiency',
    title: 'Admin workload is slowing the team down',
    audience: 'Professional services',
    photo: IMG.scenario1,
    photoCaption: 'Workflows that depend on memory rather than systems quietly drain a team\'s best hours.',
    problem:
      "Your staff are repeating manual tasks across email, documents, spreadsheets, customer enquiries, and internal follow-up. Work gets done, but it takes too long and depends too heavily on memory.",
    riskTags: ['Lost time', 'Inconsistent follow-up', 'Staff burnout', 'Missed opportunities'],
    solution:
      'Map the workflow, identify safe automation opportunities, and build a practical AI-assisted process that keeps humans in control while reducing repetitive admin load.',
    steps: [
      'Document current process and friction points',
      'Identify safe AI use cases with low operational risk',
      'Create prompt templates and escalation rules',
      'Train staff on what to automate — and what not to',
    ],
    stack: [
      { label: 'AI workflow system', icon: Workflow },
      { label: 'Secure document assistant', icon: FileText },
      { label: 'Internal knowledge base', icon: BookOpen },
      { label: 'Automation connector', icon: Bot },
    ],
    subject: 'AI Workflow & Admin Efficiency Support',
  },
  {
    id: 'cyber-safety',
    title: 'Staff are exposed to scams, phishing and unsafe tools',
    audience: 'SMEs with non-technical teams',
    photo: IMG.scenario2,
    photoCaption: 'Most cyber risk in small business starts with everyday habits — not exotic attacks.',
    problem:
      "Your people are using email, cloud storage, phones, AI tools, and customer data every day — but most have never been shown the simple habits that prevent common cyber and privacy mistakes.",
    riskTags: ['Phishing exposure', 'Weak passwords', 'Unsafe AI use', 'Customer data mistakes'],
    solution:
      'Run a baseline digital safety audit and deliver plain-English cyber safety training that helps your team reduce common risks without overwhelming them.',
    steps: [
      'Review account security, access and data handling',
      'Identify highest-risk staff behaviours and tools',
      'Deliver plain-English cyber safety guidance',
      'Set a simple internal safety standard for everyday work',
    ],
    stack: [
      { label: 'Password management system', icon: Lock },
      { label: 'Phishing awareness tool', icon: ShieldCheck },
      { label: 'Privacy audit framework', icon: FileText },
      { label: 'AI usage policy template', icon: BookOpen },
    ],
    subject: 'Cyber Safety & Staff Protection Support',
  },
  {
    id: 'marketing-funnel',
    title: 'Marketing is active, but not clearly converting',
    audience: 'Service businesses & local operators',
    photo: IMG.scenario3,
    photoCaption: 'Ads, pages and offers all working in isolation, but no one is measuring the journey end-to-end.',
    problem:
      "You have a website, content, ads or social media activity, but the message is not converting cleanly. The offer may be unclear, tracking may be incomplete, or the customer journey may not build enough trust.",
    riskTags: ['Ad spend leakage', 'Weak landing pages', 'Poor tracking', 'Unclear positioning'],
    solution:
      'Review the offer, customer journey, landing page, Meta advertising structure, creative angles and tracking setup to improve clarity, trust and commercial performance.',
    steps: [
      'Review current offer, structure and conversion path',
      'Identify message gaps and trust barriers',
      'Map Meta campaign structure and creative angles',
      'Recommend improvements before scaling spend',
    ],
    stack: [
      { label: 'Meta campaign framework', icon: Target },
      { label: 'Landing page audit system', icon: FileText },
      { label: 'Tracking event checklist', icon: ShieldCheck },
      { label: 'Creative testing model', icon: Megaphone },
    ],
    subject: 'Marketing Funnel & Digital Systems Review',
  },
];

const MASK_STRING = '\u2588\u2588\u2588\u2588\u2588\u2588\u2588\u2588\u2588\u2588\u2588';

// ----------------------------------------------------------------------------
// HOOKS
// ----------------------------------------------------------------------------
// Per-route SEO via direct DOM manipulation (no helmet dependency)
const setHeadTag = (selector, attrs) => {
  let el = document.head.querySelector(selector);
  if (!el) {
    el = document.createElement(selector.split('[')[0]);
    document.head.appendChild(el);
  }
  Object.entries(attrs).forEach(([k, v]) => el.setAttribute(k, v));
  return el;
};
const useBusinessSEO = () => {
  useEffect(() => {
    const prevTitle = document.title;
    document.title = 'Business AI & Cyber Safety Support | All Secured';
    const description =
      'Practical AI implementation, cyber safety, workflow improvement and digital marketing support for small businesses. All Secured helps teams use digital tools safely, clearly and efficiently.';
    const created = [];
    const ensure = (sel, attrs) => {
      if (!document.head.querySelector(sel)) created.push(sel);
      return setHeadTag(sel, attrs);
    };
    ensure('meta[name="description"]', { name: 'description', content: description });
    ensure('link[rel="canonical"]', { rel: 'canonical', href: PAGE_URL });
    ensure('meta[property="og:title"]', { property: 'og:title', content: 'Business AI & Cyber Safety Support | All Secured' });
    ensure('meta[property="og:description"]', { property: 'og:description', content: description });
    ensure('meta[property="og:url"]', { property: 'og:url', content: PAGE_URL });
    ensure('meta[property="og:type"]', { property: 'og:type', content: 'website' });

    const ldId = 'bcs-jsonld';
    let ld = document.getElementById(ldId);
    if (!ld) {
      ld = document.createElement('script');
      ld.id = ldId;
      ld.type = 'application/ld+json';
      document.head.appendChild(ld);
    }
    ld.textContent = JSON.stringify({
      '@context': 'https://schema.org',
      '@type': 'ProfessionalService',
      name: 'All Secured — Business AI & Cyber Safety Support',
      description,
      url: PAGE_URL,
      telephone: '+61459701393',
      email: CONTACT_EMAIL,
      areaServed: 'AU',
      provider: { '@type': 'Organization', name: 'All Secured', url: 'https://allsecured.com.au' },
      serviceType: [
        'AI Implementation', 'Cyber Safety Baseline',
        'Staff Digital Safety Training', 'Digital Marketing Review',
        'Business Process Improvement',
      ],
    });

    return () => {
      document.title = prevTitle;
      document.getElementById(ldId)?.remove();
      created.forEach((sel) => document.head.querySelector(sel)?.remove());
    };
  }, []);
};

// IntersectionObserver-driven scroll-reveal
const useScrollReveal = () => {
  useEffect(() => {
    const els = document.querySelectorAll('.bcs-reveal');
    if (!els.length || !('IntersectionObserver' in window)) {
      els.forEach((el) => el.classList.add('is-in'));
      return;
    }
    const io = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            entry.target.classList.add('is-in');
            io.unobserve(entry.target);
          }
        });
      },
      { threshold: 0.12, rootMargin: '0px 0px -40px 0px' },
    );
    els.forEach((el) => io.observe(el));
    return () => io.disconnect();
  }, []);
};

// Animated counter — counts up when target enters viewport
const AnimatedCounter = ({ to, suffix = '', duration = 1200 }) => {
  const ref = useRef(null);
  const [val, setVal] = useState(0);
  const started = useRef(false);

  useEffect(() => {
    const node = ref.current;
    if (!node) return;
    const io = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting && !started.current) {
          started.current = true;
          const start = performance.now();
          const tick = (now) => {
            const p = Math.min(1, (now - start) / duration);
            const eased = 1 - Math.pow(1 - p, 3);
            setVal(Math.round(to * eased));
            if (p < 1) requestAnimationFrame(tick);
          };
          requestAnimationFrame(tick);
        }
      },
      { threshold: 0.4 },
    );
    io.observe(node);
    return () => io.disconnect();
  }, [to, duration]);

  return (
    <span ref={ref}>
      {val}
      {suffix && <sup>{suffix}</sup>}
    </span>
  );
};

// ----------------------------------------------------------------------------
// PAGE
// ----------------------------------------------------------------------------
const BusinessAICyberSupport = () => {
  useBusinessSEO();
  useScrollReveal();

  const [activeId, setActiveId] = useState(SCENARIOS[0].id);
  const [modalSubject, setModalSubject] = useState(null);
  const scenarioRef = useRef(null);
  const enquiryRef = useRef(null);
  const tabBarRef = useRef(null);
  const heroRef = useRef(null);
  const [indicator, setIndicator] = useState({ x: 6, w: 0 });

  const activeScenario = useMemo(
    () => SCENARIOS.find((s) => s.id === activeId) || SCENARIOS[0],
    [activeId],
  );

  // Move the sliding pill indicator behind the active tab
  useEffect(() => {
    if (!tabBarRef.current) return;
    const active = tabBarRef.current.querySelector('.bcs-tab.active');
    if (!active) return;
    const barRect = tabBarRef.current.getBoundingClientRect();
    const aRect = active.getBoundingClientRect();
    setIndicator({ x: aRect.left - barRect.left, w: aRect.width });
  }, [activeId]);

  // Mouse-tracked spotlight on hero
  const onHeroMove = useCallback((e) => {
    const r = heroRef.current?.getBoundingClientRect();
    if (!r) return;
    heroRef.current.style.setProperty('--mx', `${((e.clientX - r.left) / r.width) * 100}%`);
    heroRef.current.style.setProperty('--my', `${((e.clientY - r.top) / r.height) * 100}%`);
  }, []);

  const scrollToScenarios = () =>
    scenarioRef.current?.scrollIntoView({ behavior: 'smooth', block: 'start' });
  const scrollToEnquiry = () =>
    enquiryRef.current?.scrollIntoView({ behavior: 'smooth', block: 'start' });
  const openModalForActive = () => setModalSubject(activeScenario.subject);
  const openModalGeneral = () => setModalSubject('General Business Enquiry');

  return (
    <div className="bcs-page" data-testid="bcs-page">
      {/* HEADER */}
      <header className="bcs-header">
        <div className="bcs-header-inner">
          <Link to="/" className="bcs-brand" data-testid="bcs-brand">
            <span className="bcs-brand-pulse" />
            <div className="bcs-brand-text">All Secured <span>/ business</span></div>
          </Link>
          <nav className="bcs-header-nav">
            <button className="bcs-header-link" onClick={scrollToScenarios}>Scenarios</button>
            <button className="bcs-header-link" onClick={scrollToEnquiry}>Contact</button>
            <button className="bcs-header-cta" onClick={scrollToEnquiry} data-testid="bcs-header-cta">
              Enquire <ArrowRight size={13} />
            </button>
          </nav>
        </div>
      </header>

      {/* HERO */}
      <section
        ref={heroRef}
        className="bcs-hero"
        onMouseMove={onHeroMove}
        data-testid="bcs-hero"
      >
        <div className="bcs-aurora bcs-aurora-1" />
        <div className="bcs-aurora bcs-aurora-2" />
        <div className="bcs-aurora bcs-aurora-3" />
        <div className="bcs-spotlight" />

        <div className="bcs-container">
          <div className="bcs-hero-grid">
            <div className="bcs-reveal">
              <span className="bcs-chip">
                <span className="bcs-chip-dot" />
                Available now · Cairns, AU
              </span>
              <h1 className="bcs-h1">
                Make your business <span className="bcs-h1-grad">safer, sharper</span> and more efficient.
              </h1>
              <p className="bcs-lede">
                All Secured helps small businesses implement useful AI workflows, improve
                digital safety habits, and create clearer systems — without drowning teams
                in jargon.
              </p>
              <div className="bcs-cta-row">
                <button
                  className="bcs-btn-primary"
                  onClick={scrollToScenarios}
                  data-testid="bcs-hero-primary"
                >
                  Explore scenarios <ArrowRight size={15} />
                </button>
                <button
                  className="bcs-btn-secondary"
                  onClick={scrollToEnquiry}
                  data-testid="bcs-hero-secondary"
                >
                  Enquire now
                </button>
              </div>

              <div className="bcs-stat-row">
                <div>
                  <div className="bcs-stat-v"><AnimatedCounter to={4} /></div>
                  <div className="bcs-stat-l">days · diagnostic</div>
                </div>
                <div>
                  <div className="bcs-stat-v"><AnimatedCounter to={12} suffix="+" /></div>
                  <div className="bcs-stat-l">workflows mapped</div>
                </div>
                <div>
                  <div className="bcs-stat-v"><AnimatedCounter to={0} suffix="%" /></div>
                  <div className="bcs-stat-l">jargon delivered</div>
                </div>
              </div>
            </div>

            <div className="bcs-hero-photos bcs-reveal">
              <div className="bcs-photo bcs-photo-a" />
              <div className="bcs-photo bcs-photo-b" />
              <div className="bcs-snapshot" data-testid="bcs-snapshot">
                <div className="bcs-snapshot-head">
                  <div>
                    <div className="bcs-snapshot-sub">All Secured</div>
                    <div className="bcs-snapshot-title">Business Safety Snapshot</div>
                  </div>
                  <span className="bcs-snapshot-state">Preview</span>
                </div>
                <div className="bcs-snapshot-row">
                  <span className="bcs-snapshot-key">AI use</span>
                  <span className="bcs-snapshot-val"><span className="bcs-snapshot-dot" /> Unclear rules</span>
                </div>
                <div className="bcs-snapshot-row">
                  <span className="bcs-snapshot-key">Passwords</span>
                  <span className="bcs-snapshot-val"><span className="bcs-snapshot-dot" /> Mixed habits</span>
                </div>
                <div className="bcs-snapshot-row">
                  <span className="bcs-snapshot-key">Customer data</span>
                  <span className="bcs-snapshot-val"><span className="bcs-snapshot-dot slate" /> Needs review</span>
                </div>
                <div className="bcs-snapshot-row">
                  <span className="bcs-snapshot-key">Marketing funnel</span>
                  <span className="bcs-snapshot-val"><span className="bcs-snapshot-dot" /> Tracking gap</span>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* INTRO + VALUE CARDS */}
      <section className="bcs-section" data-testid="bcs-intro">
        <div className="bcs-container">
          <div className="bcs-reveal">
            <span className="bcs-eyebrow">What we do</span>
            <h2 className="bcs-h2">
              Practical support where AI, cyber safety and business systems <em>overlap</em>.
            </h2>
            <p className="bcs-section-lede">
              Most small businesses are now using digital tools, cloud platforms, AI assistants,
              online payments, email, shared documents and social media every day. The challenge
              is not choosing more tools — it's using the right tools safely, clearly and efficiently.
            </p>
          </div>

          <div className="bcs-value-grid bcs-reveal bcs-reveal-stagger">
            {[
              {
                icon: Sparkles,
                title: 'AI that saves time',
                body: 'Practical workflows, prompt systems and automation support for repetitive business tasks.',
              },
              {
                icon: ShieldCheck,
                title: 'Cyber safety staff understand',
                body: 'Plain-English guidance for passwords, scams, privacy, account safety and safe AI use.',
              },
              {
                icon: Target,
                title: 'Sharper conversion paths',
                body: 'Review of landing pages, Meta ads, tracking, offer clarity and customer journey gaps.',
              },
            ].map(({ icon: Ico, title, body }, i) => (
              <div key={title} className="bcs-value-card" style={{ '--i': i }}>
                <div className="bcs-value-icon"><Ico size={20} /></div>
                <h3 className="bcs-value-title">{title}</h3>
                <p className="bcs-value-body">{body}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* SCENARIOS */}
      <section
        ref={scenarioRef}
        className="bcs-section bcs-section-cream bcs-scenarios"
        data-testid="bcs-scenarios"
      >
        <div className="bcs-container">
          <div className="bcs-scenario-head bcs-reveal">
            <span className="bcs-eyebrow">Three scenarios</span>
            <h2 className="bcs-h2">See how All Secured would approach the problem.</h2>
            <p className="bcs-section-lede" style={{ marginBottom: 0 }}>
              The examples below reveal enough to show the thinking — then protect the exact
              implementation stack behind a consultation gate.
            </p>
          </div>

          <div className="bcs-tab-bar bcs-reveal" ref={tabBarRef} role="tablist">
            <div
              className="bcs-tab-indicator"
              style={{ transform: `translateX(${indicator.x - 6}px)`, width: `${indicator.w}px` }}
            />
            {SCENARIOS.map((s, i) => (
              <button
                key={s.id}
                role="tab"
                aria-selected={activeId === s.id}
                className={`bcs-tab ${activeId === s.id ? 'active' : ''}`}
                onClick={() => setActiveId(s.id)}
                data-testid={`bcs-tab-${s.id}`}
              >
                <span className="bcs-tab-num">0{i + 1}</span>
                {s.title.split(' ').slice(0, 4).join(' ')}{s.title.split(' ').length > 4 ? '…' : ''}
              </button>
            ))}
          </div>

          <div
            className="bcs-scenario-panel"
            data-testid="bcs-scenario-panel"
          >
            <div
              key={`photo-${activeScenario.id}`}
              className="bcs-scenario-photo bcs-scenario-photo-fade"
              style={{ backgroundImage: `url(${activeScenario.photo})` }}
            >
              <div className="bcs-scenario-photo-tag">
                <span style={{ width: 6, height: 6, borderRadius: 999, background: 'var(--teal)' }} />
                {activeScenario.audience}
              </div>
              <div className="bcs-scenario-photo-foot">
                <strong>{activeScenario.photoCaption}</strong>
              </div>
            </div>

            <div key={`body-${activeScenario.id}`} className="bcs-scenario-body bcs-scenario-body-fade">
              <span className="bcs-scenario-audience">
                Scenario · {SCENARIOS.findIndex((s) => s.id === activeId) + 1} of {SCENARIOS.length}
              </span>
              <h3 className="bcs-scenario-title">{activeScenario.title}</h3>
              <p className="bcs-scenario-problem">{activeScenario.problem}</p>

              <div className="bcs-risk-tags">
                {activeScenario.riskTags.map((tag) => (
                  <span key={tag} className="bcs-risk-tag">{tag}</span>
                ))}
              </div>

              <div className="bcs-scenario-approach">How we'd approach it</div>
              <p className="bcs-scenario-approach-body">{activeScenario.solution}</p>

              <ol className="bcs-steps">
                {activeScenario.steps.map((step, i) => (
                  <li key={i} className="bcs-step">
                    <span className="bcs-step-num">{i + 1}</span>
                    <span>{step}</span>
                  </li>
                ))}
              </ol>

              <div className="bcs-stack-wrap">
                <div className="bcs-stack-head">
                  <Lock size={13} /> Implementation stack — protected
                </div>
                <div className="bcs-stack-grid">
                  {activeScenario.stack.map(({ label, icon: Ico }) => (
                    <button
                      key={label}
                      type="button"
                      className="bcs-stack-card"
                      onClick={openModalForActive}
                      data-testid={`bcs-stack-${label.replace(/\s+/g, '-').toLowerCase()}`}
                    >
                      <div className="bcs-stack-icon"><Ico size={15} /></div>
                      <div className="bcs-stack-body">
                        <div className="bcs-stack-label">{label}</div>
                        <div className="bcs-stack-mask" aria-hidden="true">{MASK_STRING}</div>
                      </div>
                      <Lock size={13} className="bcs-stack-lock" />
                    </button>
                  ))}
                </div>

                <button
                  type="button"
                  className="bcs-unlock-cta"
                  onClick={openModalForActive}
                  data-testid="bcs-unlock-cta"
                >
                  Unlock implementation details <ArrowRight size={14} />
                </button>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* SERVICES */}
      <section className="bcs-section" data-testid="bcs-services">
        <div className="bcs-container">
          <div className="bcs-reveal">
            <span className="bcs-eyebrow">Services</span>
            <h2 className="bcs-h2">
              Support that combines AI, safety, marketing and plain-English training.
            </h2>
          </div>

          <div className="bcs-services-grid bcs-reveal bcs-reveal-stagger">
            {[
              { icon: Sparkles, title: 'AI Implementation', body: 'Practical AI workflows, prompt systems, internal knowledge support, automation mapping, and safe-use guidance for teams.' },
              { icon: ShieldCheck, title: 'Cyber Safety Baseline', body: 'Plain-English security checks for accounts, devices, staff habits, data handling, password systems and scam awareness.' },
              { icon: BookOpen, title: 'Training & Documentation', body: 'Short internal guides, checklists, scripts, policy explainers, onboarding assets, and customer education materials.' },
              { icon: Megaphone, title: 'Digital Marketing Review', body: 'Meta ads structure, landing page clarity, offer positioning, tracking readiness, creative angles and conversion path review.' },
            ].map(({ icon: Ico, title, body }, i) => (
              <div key={title} className="bcs-service-card" style={{ '--i': i }}>
                <div className="bcs-service-icon"><Ico size={20} /></div>
                <h3 className="bcs-service-title">{title}</h3>
                <p className="bcs-service-body">{body}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* USE CASES + BEST FIT */}
      <section className="bcs-section bcs-section-cream" data-testid="bcs-usecases">
        <div className="bcs-container">
          <div className="bcs-reveal">
            <span className="bcs-eyebrow">Real situations</span>
            <h2 className="bcs-h2">Common business problems All Secured can help with.</h2>
          </div>

          <div className="bcs-usecase-grid bcs-reveal">
            {[
              'Staff using AI tools without clear rules',
              'Repetitive admin tasks slowing the team down',
              'Weak password and account security habits',
              'Confusing or inconsistent customer follow-up',
              'Poorly documented internal processes',
              'Marketing campaigns running without clear tracking',
              'Landing pages that do not clearly explain the offer',
              'Customer data being handled without simple safety rules',
              'Staff unsure how to identify scams or phishing attempts',
              'Businesses needing plain-English digital safety training',
            ].map((line) => (
              <div className="bcs-usecase" key={line}>
                <span className="bcs-usecase-tick"><Check size={11} /></span>
                <span>{line}</span>
              </div>
            ))}
          </div>

          <div className="bcs-bestfit-row bcs-reveal">
            <div className="bcs-bestfit-photo">
              <div className="bcs-bestfit-photo-tag">
                <span className="bcs-eyebrow">Who this is for</span>
                <h3>Teams that want practical capability, not theory.</h3>
              </div>
            </div>
            <div className="bcs-bestfit-card">
              <h3>Best-fit businesses</h3>
              <ul className="bcs-bestfit-list">
                {[
                  'Small to mid-sized teams wanting practical AI adoption',
                  'Businesses handling customer data or online payments',
                  'Service firms that need clearer systems and documentation',
                  'Marketing teams wanting sharper funnels and safer tooling',
                  'Businesses needing remote support without hiring a full-time specialist',
                ].map((line) => (
                  <li key={line} className="bcs-bestfit-item">
                    <span className="bcs-bestfit-tick"><Check size={12} /></span>
                    <span>{line}</span>
                  </li>
                ))}
              </ul>
            </div>
          </div>
        </div>
      </section>

      {/* FINAL CTA */}
      <section ref={enquiryRef} className="bcs-final" data-testid="bcs-final">
        <div className="bcs-container">
          <div className="bcs-final-inner bcs-reveal">
            <span className="bcs-eyebrow">Talk to All Secured</span>
            <h2 className="bcs-final-title">
              Need a custom AI or cyber safety implementation plan?
            </h2>
            <p className="bcs-final-body">
              Share the problem, current tools, staff size and desired outcome. All Secured will
              review the situation and recommend a practical next step.
            </p>
            <div className="bcs-final-cta-row">
              <button
                type="button"
                className="bcs-final-btn-primary"
                onClick={openModalGeneral}
                data-testid="bcs-final-enquire"
              >
                <Mail size={16} /> Send an enquiry
              </button>
              <a className="bcs-final-btn-ghost" href={CONTACT_PHONE_TEL} data-testid="bcs-final-call">
                <Phone size={16} /> Call {CONTACT_PHONE_DISPLAY}
              </a>
            </div>
            <div className="bcs-final-contacts">
              <a href={`mailto:${CONTACT_EMAIL}`}>{CONTACT_EMAIL}</a>
              <a href={CONTACT_PHONE_TEL}>{CONTACT_PHONE_DISPLAY}</a>
            </div>
            <div className="bcs-final-trust">
              Practical digital capability · Clear communication · Commercial instinct
            </div>
          </div>
        </div>
      </section>

      {modalSubject && (
        <EnquiryModal
          defaultSubject={modalSubject}
          scenarioId={activeId}
          onClose={() => setModalSubject(null)}
        />
      )}
    </div>
  );
};

// ----------------------------------------------------------------------------
// ENQUIRY MODAL (same backend integration, restyled to match)
// ----------------------------------------------------------------------------
const EnquiryModal = ({ defaultSubject, scenarioId, onClose }) => {
  const [form, setForm] = useState({
    name: '', business_name: '', email: '', phone: '',
    subject: defaultSubject, message: '',
  });
  const [submitting, setSubmitting] = useState(false);
  const [done, setDone] = useState(false);
  const [error, setError] = useState('');

  useEffect(() => {
    const onKey = (e) => e.key === 'Escape' && onClose();
    window.addEventListener('keydown', onKey);
    document.body.style.overflow = 'hidden';
    return () => {
      window.removeEventListener('keydown', onKey);
      document.body.style.overflow = '';
    };
  }, [onClose]);

  const update = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

  const buildMailto = () => {
    const body = encodeURIComponent(
      `Name: ${form.name}\nBusiness: ${form.business_name}\nEmail: ${form.email}\nPhone: ${form.phone}\n\n${form.message}`,
    );
    return `mailto:${CONTACT_EMAIL}?subject=${encodeURIComponent(form.subject)}&body=${body}`;
  };

  const submit = async (e) => {
    e?.preventDefault?.();
    setError('');
    if (!form.name.trim()) return setError('Please add your name.');
    if (!/^\S+@\S+\.\S+$/.test(form.email)) return setError('Please add a valid email.');
    if (!form.message.trim()) return setError('Please add a short message.');

    setSubmitting(true);
    try {
      await axios.post(`${API_URL}/api/business/enquiry`, { ...form, scenario_id: scenarioId });
      setDone(true);
    } catch (err) {
      const detail = err?.response?.data?.detail || 'Could not submit. Opening your email client as a backup.';
      setError(detail);
      window.location.href = buildMailto();
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div
      className="bcs-modal-backdrop"
      role="dialog"
      aria-modal="true"
      aria-label="Enquire about this solution"
      onClick={(e) => e.target === e.currentTarget && onClose()}
      data-testid="bcs-modal"
    >
      <div className="bcs-modal">
        <button
          type="button"
          className="bcs-modal-close"
          onClick={onClose}
          aria-label="Close enquiry form"
          data-testid="bcs-modal-close"
        >
          <X size={16} />
        </button>

        {!done ? (
          <>
            <div className="bcs-modal-eyebrow">All Secured · Business enquiry</div>
            <h3 className="bcs-modal-title">Enquire about this solution</h3>
            <p className="bcs-modal-intro">
              Tell us a little about your business and what you would like to improve. All Secured
              will review your enquiry and recommend the most practical next step.
            </p>

            <form onSubmit={submit} className="bcs-form" data-testid="bcs-form">
              <div className="bcs-form-grid">
                <div className="bcs-form-field">
                  <label className="bcs-form-label" htmlFor="bcs-name">Name</label>
                  <input id="bcs-name" className="bcs-form-input" value={form.name} onChange={update('name')} autoComplete="name" required data-testid="bcs-input-name" />
                </div>
                <div className="bcs-form-field">
                  <label className="bcs-form-label" htmlFor="bcs-business">Business name</label>
                  <input id="bcs-business" className="bcs-form-input" value={form.business_name} onChange={update('business_name')} autoComplete="organization" data-testid="bcs-input-business" />
                </div>
                <div className="bcs-form-field">
                  <label className="bcs-form-label" htmlFor="bcs-email">Email</label>
                  <input id="bcs-email" type="email" className="bcs-form-input" value={form.email} onChange={update('email')} autoComplete="email" required data-testid="bcs-input-email" />
                </div>
                <div className="bcs-form-field">
                  <label className="bcs-form-label" htmlFor="bcs-phone">Phone number</label>
                  <input id="bcs-phone" className="bcs-form-input" value={form.phone} onChange={update('phone')} autoComplete="tel" data-testid="bcs-input-phone" />
                </div>
                <div className="bcs-form-field full">
                  <label className="bcs-form-label" htmlFor="bcs-subject">Subject</label>
                  <input id="bcs-subject" className="bcs-form-input" value={form.subject} onChange={update('subject')} required data-testid="bcs-input-subject" />
                </div>
                <div className="bcs-form-field full">
                  <label className="bcs-form-label" htmlFor="bcs-message">What would you like help with?</label>
                  <textarea
                    id="bcs-message"
                    className="bcs-form-textarea"
                    value={form.message}
                    onChange={update('message')}
                    placeholder="Please briefly describe your current situation, the tools your team uses, and what outcome you would like to achieve."
                    required
                    data-testid="bcs-input-message"
                  />
                </div>
              </div>

              {error && <div className="bcs-form-error" data-testid="bcs-form-error">{error}</div>}

              <button type="submit" className="bcs-form-submit" disabled={submitting} data-testid="bcs-submit">
                {submitting ? (
                  <><Loader2 size={16} className="bcs-spin" /> Sending…</>
                ) : (
                  <>Send enquiry <Send size={15} /></>
                )}
              </button>
            </form>

            <div className="bcs-modal-alt">
              <strong>Prefer to speak directly?</strong> Call Nick on{' '}
              <a href={CONTACT_PHONE_TEL}>{CONTACT_PHONE_DISPLAY}</a>{' '}
              or email <a href={`mailto:${CONTACT_EMAIL}`}>{CONTACT_EMAIL}</a>.
            </div>
          </>
        ) : (
          <div className="bcs-modal-success" data-testid="bcs-modal-success">
            <div className="bcs-modal-success-icon"><CheckCircle2 size={32} /></div>
            <h3 className="bcs-modal-title">Thank you — your enquiry has been received.</h3>
            <p className="bcs-modal-intro">
              Nick will review your situation and respond with the most practical next step.
            </p>
            <button type="button" className="bcs-form-submit" onClick={onClose} data-testid="bcs-modal-success-close">
              Close
            </button>
          </div>
        )}
      </div>
    </div>
  );
};

export default BusinessAICyberSupport;
